import type { GuestImportDraft, GuestImportRow, ImportIssue, ImportMapping, PlannerWorkspace, WeddingEvent } from "../domain/types";

/**
 * Real CSV parsing for the guest import wizard (RFC4180-ish): handles quoted fields, embedded
 * commas/newlines inside quotes, escaped quotes (""), and both CRLF and LF line endings. Linear in
 * the input length, so 500 realistic guest rows parse in single-digit milliseconds -- no chunking
 * or web worker needed to keep the browser responsive.
 */
export function parseCsvText(text: string): string[][] {
  // Strip a UTF-8 BOM if the file was saved with one (common from Excel) -- not doing this would
  // silently corrupt the first header's name (`﻿Full name` fails every mapping match).
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const len = clean.length;
  while (i < len) {
    const char = clean[i];
    if (inQuotes) {
      if (char === '"') {
        if (clean[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += char;
      i++;
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (char === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (char === "\r") {
      i++;
      continue;
    }
    if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }
    field += char;
    i++;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

const BASE_TARGET_FIELDS = ["Full name", "Household", "Group", "Phone", "Email", "Dietary requirement", "Not imported"];

const HEADER_SYNONYMS: Record<string, string[]> = {
  "Full name": ["full name", "guest name", "name of guest", "name"],
  Household: ["household", "family name", "family"],
  Group: ["group", "side", "relationship"],
  Phone: ["phone", "mobile", "cell", "contact number"],
  Email: ["email", "e-mail"],
  "Dietary requirement": ["dietary", "diet", "allergy", "allergies", "meal requirement"]
};

/** Per-event target field label, e.g. "Mehndi (RSVP)" -- a column with this exact header (or a
 * close match) maps a Y/N-style value directly to that celebration's invite flag. Kept distinct
 * from the generic "Celebration invitations" free-text fallback below. */
function eventTargetField(event: WeddingEvent): string {
  return `${event.name} (RSVP)`;
}

export function targetFieldsFor(events: WeddingEvent[]): string[] {
  return [...BASE_TARGET_FIELDS.slice(0, 3), ...events.map(eventTargetField), "Celebration invitations", ...BASE_TARGET_FIELDS.slice(3)];
}

function guessTargetField(header: string, events: WeddingEvent[]): string {
  const normalized = header.trim().toLowerCase();
  if (!normalized) return "Not imported";
  const eventMatch = events.find((event) => event.name.toLowerCase() === normalized);
  if (eventMatch) return eventTargetField(eventMatch);
  for (const [target, synonyms] of Object.entries(HEADER_SYNONYMS)) {
    if (synonyms.some((synonym) => normalized.includes(synonym))) return target;
  }
  if (normalized.includes("celebration") || normalized.includes("invited")) return "Celebration invitations";
  return "Not imported";
}

const TRUTHY = new Set(["y", "yes", "1", "true", "attending", "x"]);

/** Builds a template CSV (one Y/N column per real celebration) so a couple's own spreadsheet can
 * be shaped to auto-map cleanly, without requiring it. */
export function buildImportTemplateCsv(events: WeddingEvent[]): string {
  const headers = ["Full name", "Household", "Group", "Phone", "Email", ...events.map((event) => event.name), "Dietary requirement"];
  const sample = ["Ahmed Khan", "Khan", "Bride's family", "+92 300 1234567", "ahmed@example.com", ...events.map(() => "Y"), "Nut allergy"];
  return [headers, sample].map((row) => row.map(csvEscape).join(",")).join("\r\n");
}

function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

interface ParsedGuestRow {
  rowNumber: number;
  fullName: string;
  household: string;
  group: string;
  phone: string;
  email: string;
  dietary: string;
  invitedEventIds: string[];
}

function buildRows(dataRows: string[][], mappings: ImportMapping[], events: WeddingEvent[]): ParsedGuestRow[] {
  const eventFieldToId = new Map(events.map((event) => [eventTargetField(event), event.id]));
  const eventNameToId = new Map(events.map((event) => [event.name.toLowerCase(), event.id]));
  return dataRows.map((cells, index) => {
    let fullName = "", household = "", group = "", phone = "", email = "", dietary = "";
    const invitedEventIds: string[] = [];
    mappings.forEach((mapping, columnIndex) => {
      const value = (cells[columnIndex] || "").trim();
      if (!value) return;
      if (mapping.targetField === "Full name") fullName = value;
      else if (mapping.targetField === "Household") household = value;
      else if (mapping.targetField === "Group") group = value;
      else if (mapping.targetField === "Phone") phone = value;
      else if (mapping.targetField === "Email") email = value;
      else if (mapping.targetField === "Dietary requirement") dietary = value;
      else if (mapping.targetField === "Celebration invitations") {
        value.split(/[,/]/).map((part) => part.trim().toLowerCase()).forEach((name) => {
          const eventId = eventNameToId.get(name);
          if (eventId) invitedEventIds.push(eventId);
        });
      } else if (eventFieldToId.has(mapping.targetField)) {
        if (TRUTHY.has(value.trim().toLowerCase())) invitedEventIds.push(eventFieldToId.get(mapping.targetField)!);
      }
    });
    return { rowNumber: index + 2, fullName, household, group: group || "Guest", phone, email, dietary, invitedEventIds: Array.from(new Set(invitedEventIds)) };
  });
}

/** Builds the full draft from raw CSV text: parses, auto-maps columns to target fields by header
 * name, converts each data row into a GuestImportRow, and flags issues for manual review (missing
 * name, an exact-name match against an existing guest, or an exact-name repeat within this same
 * file) -- never auto-resolved, per the no-fuzzy-automatic-dedupe rule. */
export function buildImportDraft(fileName: string, csvText: string, workspace: PlannerWorkspace): GuestImportDraft {
  const table = parseCsvText(csvText);
  if (table.length === 0) {
    return { fileName, rowsFound: 0, mappings: [], issues: [], resolutions: {}, rows: [] };
  }
  const [header, ...dataRows] = table;
  const mappings: ImportMapping[] = header.map((sourceColumn, index) => ({
    sourceColumn: sourceColumn.trim() || `Column ${index + 1}`,
    targetField: guessTargetField(sourceColumn, workspace.events),
    sample: (dataRows[0]?.[index] || "").trim()
  }));

  const parsed = buildRows(dataRows, mappings, workspace.events);
  const existingByName = new Map(workspace.people.map((person) => [person.name.trim().toLowerCase(), person]));
  const seenInFile = new Map<string, number>();
  const issues: ImportIssue[] = [];
  const rows: GuestImportRow[] = parsed.map((parsedRow) => {
    const key = parsedRow.fullName.trim().toLowerCase();
    const base: GuestImportRow = {
      rowNumber: parsedRow.rowNumber,
      resolution: "IMPORT",
      rawValue: parsedRow.fullName || undefined,
      household: parsedRow.household ? { name: parsedRow.household } : undefined,
      person: { fullName: parsedRow.fullName, relationshipLabel: parsedRow.group, email: parsedRow.email || undefined, phone: parsedRow.phone || undefined, notes: parsedRow.dietary ? `Dietary: ${parsedRow.dietary}` : undefined },
      invitedEventIds: parsedRow.invitedEventIds
    };

    if (!key) {
      const issueId = `row-${parsedRow.rowNumber}`;
      issues.push({ id: issueId, row: parsedRow.rowNumber, value: "(blank)", issue: "No name in this row", options: ["Skip this row"] });
      return { ...base, resolution: "SKIP", issue: issueId };
    }

    const existing = existingByName.get(key);
    if (existing) {
      const issueId = `row-${parsedRow.rowNumber}`;
      issues.push({ id: issueId, row: parsedRow.rowNumber, value: parsedRow.fullName, issue: `Matches an existing guest (${existing.name})`, options: [`Merge into ${existing.name}`, "Import as new guest", "Skip this row"] });
      return { ...base, issue: issueId, mergeIntoPersonId: existing.id };
    }

    const firstSeenRow = seenInFile.get(key);
    if (firstSeenRow !== undefined) {
      const issueId = `row-${parsedRow.rowNumber}`;
      issues.push({ id: issueId, row: parsedRow.rowNumber, value: parsedRow.fullName, issue: `Same name appears earlier in this file (row ${firstSeenRow})`, options: ["Import both", "Skip this row"] });
      return { ...base, issue: issueId };
    }

    // Every guest needs a household -- it's the unit RSVP links, invitations and addressing are
    // built around, and the write endpoint refuses to create a person without one. Flagged here as
    // a normal, skippable per-row issue (same shape as a blank name) so one incomplete row no
    // longer fails the entire file at final submit -- the rest of the list still imports, and this
    // guest can be fixed and re-uploaded on their own.
    if (!parsedRow.household) {
      const issueId = `row-${parsedRow.rowNumber}`;
      issues.push({ id: issueId, row: parsedRow.rowNumber, value: parsedRow.fullName, issue: "No household given for this guest", options: ["Skip this row"] });
      return { ...base, resolution: "SKIP", issue: issueId };
    }

    seenInFile.set(key, parsedRow.rowNumber);
    return base;
  });

  return { fileName, rowsFound: rows.length, mappings, issues, resolutions: {}, rows };
}

/** Applies each issue's chosen resolution label onto its row's structured resolution field, right
 * before final submit. Rows with no issue (or an issue not yet resolved) pass through untouched --
 * the wizard blocks submit until every issue has a resolution. */
export function resolveRowsForSubmit(draft: GuestImportDraft): GuestImportRow[] {
  const issueById = new Map(draft.issues.map((issue) => [issue.id, issue]));
  return (draft.rows || []).map((row) => {
    if (!row.issue) return row;
    const issue = issueById.get(row.issue);
    const chosen = issue ? draft.resolutions[issue.id] : undefined;
    if (!chosen) return row;
    if (chosen.startsWith("Skip")) return { ...row, resolution: "SKIP" };
    if (chosen.startsWith("Merge into")) return { ...row, resolution: "MERGE" };
    return { ...row, resolution: "IMPORT" };
  });
}
